"use client";

import { storyblokEditable } from "@storyblok/react";
import { sbBlok } from "@/types/sb-types";
import clsx from "clsx";
import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  NODE_LI,
  NODE_OL,
  NODE_PARAGRAPH,
  NODE_UL,
  MARK_LINK,
  render,
} from "storyblok-rich-text-react-renderer";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const heightVariants = {
  initial: {
    height: 0,
  },
  open: {
    height: "auto",
  },
};

const Faq = ({ blok }: sbBlok) => {
  const [openItem, setOpenItem] = useState<any>(null);

  return (
    <section {...storyblokEditable(blok)}>
      <div
        className={clsx("grid-container mt-10 mb-5 2xl:mt-[70px] 2xl:mb-10", {
          "pt-7 lg:pt-14":
            !!blok.spacing_top?.length &&
            blok.spacing_top[0].option === "large",
          "pt-14 lg:pt-[120px]":
            !!blok.spacing_top?.length &&
            blok.spacing_top[0].option === "x_large",
          "pt-[120px] lg:pt-[180px]":
            !!blok.spacing_top?.length &&
            blok.spacing_top[0].option === "xx_large",
          "pb-7 lg:pb-14":
            !!blok.spacing_bottom?.length &&
            blok.spacing_bottom[0].option === "large",
          "pb-14 lg:pb-[120px]":
            !!blok.spacing_bottom?.length &&
            blok.spacing_bottom[0].option === "x_large",
          "pb-[120px] lg:pb-[180px]":
            !!blok.spacing_bottom?.length &&
            blok.spacing_bottom[0].option === "xx_large",
        })}
      >
        <div className="col-start-1 col-span-4 row-start-1 lg:col-start-3 lg:col-span-12 2xl:col-start-7 2xl:col-span-20">
          {blok.title && <h1 className="mt-0 mb-5 2xl:mb-7.5">{blok.title}</h1>}
          {blok.content.length > 0 && (
            <div className="flex flex-col gap-2.5 2xl:gap-[15px]">
              {blok.content.map((blok: sbBlok) => {
                return (
                  <div
                    key={blok._uid}
                    className="cursor-pointer rounded-[10px] bg-tm-c-gray-800 py-3.5 pr-7 pl-[35px]"
                    onClick={() =>
                      setOpenItem(openItem === blok._uid ? null : blok?._uid)
                    }
                  >
                    <div>
                      <div className="flex justify-between items-center rounded-[10px] text-[15px] tracking-[0.45px] font-normal lg:text-[20px] lg:tracking-[0.6px]">
                        <div>{blok.title}</div>
                        <motion.div
                          animate={{
                            rotate: openItem === blok._uid ? 180 : 0,
                          }}
                          transition={{
                            ease: "easeInOut",
                          }}
                        >
                          <ExpandMoreIcon fontSize="large" />
                        </motion.div>
                      </div>
                    </div>

                    <motion.div
                      variants={heightVariants}
                      animate={openItem === blok._uid ? "open" : "initial"}
                      className="h-0 overflow-hidden bg-tm-c-gray-800 rounded-b-[10px]"
                    >
                      <div>
                        {render(blok?.content, {
                          nodeResolvers: {
                            [NODE_PARAGRAPH]: (children) => (
                              <p className="text-[15px] xl:text-[18px] xl:tracking-[0.6px] leading-relaxed font-light">
                                {children}
                              </p>
                            ),
                            [NODE_UL]: (children) => (
                              <ul className="pl-14 list-disc">{children}</ul>
                            ),
                            [NODE_OL]: (children) => (
                              <ol className="pl-14 list-decimal">{children}</ol>
                            ),
                            [NODE_LI]: (children) => (
                              <li className="list-item">{children}</li>
                            ),
                          },
                          markResolvers: {
                            [MARK_LINK]: (children, props) => {
                              return (
                                <a
                                  href={props?.href}
                                  target={props?.target}
                                  className="text-tm-c-brand no-underline"
                                >
                                  {children}
                                </a>
                              );
                            },
                          },
                        })}
                      </div>
                    </motion.div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Faq;
